// src/iTwin/queries/drainageUtilitiesQueries.ts
import type { IModelConnection } from "@itwin/core-frontend";
import { QueryBinder, QueryRowFormat } from "@itwin/core-common";
import { DrainageUtilities } from "../../../domain/utilities/DrainageUtilities";


const ECSQL_DRAINAGE_UTILITIES_NODE = `
  SELECT
    n.ActiveReference                      AS ActiveReference,
    n.ActiveStationOffsetReference         AS ActiveStationOffsetReference,
    n.BaselineOffset                       AS BaselineOffset,
    n.BaselineStation                      AS BaselineStation,
    n.BypassedFlow                         As BypassedFlow,
    n.CapturedFlow                         AS CapturedFlow,
    n.CIF_ID                               AS CIFID,
    n.CurbOpeningLength                    AS CurbOpeningLength,
    n.DelineationReference                 AS DelineationReference,
    n.Depth                                AS Depth,
    n.Diameter                             AS Diameter,
    n.ECClassId                            AS ECClassID,
    n.ECInstanceId                         AS ECInstanceID,
    n.Element                              AS Element,
    n.ElementType                          AS ElementType,
    n.ElevationIsInvert                    AS ElevationIsInvert,
    n.GrateLength                          AS GrateLength,
    n.GrateWidth                           AS GrateWidth,
    n.GroundElevation                      AS GroundElevation,
    n.IndependentConduitElevations         AS IndependentConduitElevations,
    n.InvertElevation                      AS InvertElevation,
    n.IsActive                             AS IsActive,
    n.Length                               AS Length,
    n.LongitudinalSlope                    AS LongitudinalSlope,
    n.MatchSlopeofConduit                  AS MatchSlopeOfConduit,
    n.NetworkType                          AS NetworkType,
    n.OrientTopToSurface                   AS OrientTopToSurface,
    n.PROP_OnlyIncludeContributingSlopes   AS PROPOnlyIncludeContributingSlopes,
    n.PROP_Owner                           AS PROPOwner,
    n.PROP_PROP_CrosssectionOffset         AS PROPPROPCrosssectionOffset,
    n.RimElevation                         AS RimElevation,
    n.RoadCrossSlopeDistance               AS RoadCrossSlopeDistance,
    n.SetOutElevation                      AS SetOutElevation,
    n.SetOutX                              AS SetOutX,
    n.SetOutY                              AS SetOutY,
    n.Spread                               AS Spread,
    n.UseLongitudinalSlope                 AS UseLongitudinalSlope,
    n.UserDefinedElementType               AS UserDefinedElementType,
    n.UserDefinedNetworkType               AS UserDefinedNetworkType,
    n.UseRoadCrossSlope                    AS UseRoadSlope,
    n.Width                                AS Width
  FROM CifSubsurface.NodeAspect n
  WHERE n.Element.Id = ?
`;

const ECSQL_DRAINAGE_UTILITIES_LINK = `
  SELECT
    NULL                                   AS ActiveReference,
    NULL                                   AS ActiveStationOffsetReference,
    NULL                                   AS BaselineOffset,
    NULL                                   AS BaselineStation,
    NULL                                   AS BypassedFlow,
    l.pROP_SU_Flow                          AS CapturedFlow,
    l.cIF_ID                               AS CIFID,
    NULL                                   AS CurbOpeningLength,
    NULL                                   AS DelineationReference,
    l.pROP_SU_Depth                        AS Depth,
    COALESCE(l.pROP_SU_Diameter, l.pROP_SU_DiameterValue) AS Diameter,
    l.ECClassId                            AS ECClassID,
    l.ECInstanceId                         AS ECInstanceID,
    l.Element.Id                           AS Element,
    l.elementType                          AS ElementType,
    NULL                                   AS ElevationIsInvert,
    NULL                                   AS GrateLength,
    NULL                                   AS GrateWidth,
    NULL                                   AS GroundElevation,
    NULL                                   AS IndependentConduitElevations,
    NULL                                   AS InvertElevation,
    l.isActive                             AS IsActive,
    COALESCE(l.length, l.pROP_SU_ConstructionLength) AS Length,
    l.calculatedSlope                      AS LongitudinalSlope,
    NULL                                   AS MatchSlopeOfConduit,
    l.networkType                          AS NetworkType,
    NULL                                   AS OrientTopToSurface,
    NULL                                   AS PROPOnlyIncludeContributingSlopes,
    l.pROP_SU_Owner                         AS PROPOwner,
    NULL                                   AS PROPPROPCrosssectionOffset,
    NULL                                   AS RimElevation,
    NULL                                   AS RoadCrossSlopeDistance,
    NULL                                   AS SetOutElevation,
    NULL                                   AS SetOutX,
    NULL                                   AS SetOutY,
    COALESCE(l.pROP_SU_SpreadStart, l.pROP_SU_SpreadStop) AS Spread,
    NULL                                   AS UseLongitudinalSlope,
    l.userDefinedElementType               AS UserDefinedElementType,
    l.userDefinedNetworkType               AS UserDefinedNetworkType,
    NULL                                   AS UseRoadSlope,
    NULL                                   AS Width
  FROM CifSubsurface.CLASS_SU_LinkAspect l
  WHERE l.Element.Id = ?
`;


export async function queryAspectClassNamesForElement(
  iModel: IModelConnection,
  elementId: string
): Promise<string[]> {
  const classNames: string[] = [];
  try {
    const readFromRel = async (relClass: string) => {
      const ecsql = `
        SELECT DISTINCT
          c.Name AS ClassName
        FROM ${relClass} rel
        JOIN BisCore.ElementAspect a
          ON a.ECInstanceId = rel.TargetECInstanceId
        LEFT JOIN meta.ECClassDef c
          ON c.ECInstanceId = a.ECClassId
        WHERE rel.SourceECInstanceId = ?
      `;
      const reader = iModel.createQueryReader(
        ecsql,
        QueryBinder.from([elementId]),
        { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
      );

      for await (const row of reader) {
        const r = (row as any).toRow?.() ?? row;
        const name = r?.ClassName as string | undefined;
        if (name && !classNames.includes(name)) classNames.push(name);
      }
    };

    await readFromRel("BisCore.ElementOwnsMultiAspects");
    await readFromRel("BisCore.ElementOwnsUniqueAspect");
  } catch (err) {
    console.warn("[DrainageUtilities] Failed to list aspect classes:", err);
  }
  return classNames;
}

export async function queryAspectClassIdsForElement(
  iModel: IModelConnection,
  elementId: string
): Promise<string[]> {
  const classIds: string[] = [];
  try {
    const readFromRel = async (relClass: string) => {
      const ecsql = `
        SELECT DISTINCT
          a.ECClassId AS ClassId
        FROM ${relClass} rel
        JOIN BisCore.ElementAspect a
          ON a.ECInstanceId = rel.TargetECInstanceId
        WHERE rel.SourceECInstanceId = ?
      `;
      const reader = iModel.createQueryReader(
        ecsql,
        QueryBinder.from([elementId]),
        { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
      );

      for await (const row of reader) {
        const r = (row as any).toRow?.() ?? row;
        const id = r?.ClassId as string | undefined;
        if (id && !classIds.includes(id)) classIds.push(id);
      }
    };

    await readFromRel("BisCore.ElementOwnsMultiAspects");
    await readFromRel("BisCore.ElementOwnsUniqueAspect");
  } catch (err) {
    console.warn("[DrainageUtilities] Failed to list aspect class ids:", err);
  }
  return classIds;
}

export async function logAspectClassesForElement(
  iModel: IModelConnection,
  elementId: string
): Promise<void> {
  const classes = await queryAspectClassNamesForElement(iModel, elementId);
  if (classes.length === 0) {
    const ids = await queryAspectClassIdsForElement(iModel, elementId);
    if (ids.length > 0) {
      console.warn(
        "[DrainageUtilities] ElementAspect class ids for",
        elementId,
        ids
      );
    } else {
      console.warn("[DrainageUtilities] No ElementAspect classes found for element", elementId);
    }
    return;
  }
  console.warn("[DrainageUtilities] ElementAspect classes for", elementId, classes);
}

export async function queryDrainageUtilitiesForElement(
  iModel: IModelConnection,
  elementId: string,
  options?: { debug?: boolean }
): Promise<DrainageUtilities | undefined> {
  const readFirst = async (ecsql: string): Promise<DrainageUtilities | undefined> => {
    try {
      const reader = iModel.createQueryReader(
        ecsql,
        QueryBinder.from([elementId]),
        { rowFormat: QueryRowFormat.UseECSqlPropertyNames }
      );

      for await (const row of reader) {
        const r = (row as any).toRow?.() ?? row;
        return DrainageUtilities.fromObject(r as Partial<DrainageUtilities>);
      }
    } catch (err) {
      console.warn("[DrainageUtilities] Query failed:", err);
    }

    return undefined;
  };

  const fromNode = await readFirst(ECSQL_DRAINAGE_UTILITIES_NODE);
  if (fromNode) return fromNode;

  const fromLink = await readFirst(ECSQL_DRAINAGE_UTILITIES_LINK);
  if (fromLink) return fromLink;

  if (options?.debug) {
    await logAspectClassesForElement(iModel, elementId);
  }

  return undefined;
}
